import { cx } from 'class-variance-authority';
import React, {
  cloneElement,
  forwardRef,
  HTMLInputTypeAttribute,
  ReactElement,
  useState,
} from 'react';
import { Box } from '../Box';
import { BaseTypeMap, Component, ComponentProps } from '../types/Component';
import * as styles from './styles';
import type { StyleProps } from './styles';
import { Label } from '../Label';
import { InputError } from '../InputError';
import { hasError } from '../utils/error';
import { Typography } from '../Typography';
import { Eye } from '@oresundsbron/icons';
import { EyeSlash } from '@oresundsbron/icons';

type InputTypeMap = BaseTypeMap<
  'input',
  Omit<StyleProps, 'intent'> & {
    startAdornment?: ReactElement<{ disabled?: boolean }>;
    endAdornment?: ReactElement<{ disabled?: boolean }>;
    label: string | ReactElement;
    name: string;
    error?: string | string[];
    id?: string;
    helpText?: string;
  }
>;

export type InputProps = ComponentProps<InputTypeMap>;

const getStyleType = (type: HTMLInputTypeAttribute): StyleProps['intent'] => {
  switch (type) {
    case 'text':
      return 'text';
    case 'checkbox':
      return 'checkbox';
    case 'radio':
      return 'radio';
    default:
      return 'text';
  }
};

const isRadioOrCheck = (type: HTMLInputTypeAttribute) =>
  ['checkbox', 'radio'].includes(type);

const isRadioReadOnly = (
  type: HTMLInputTypeAttribute,
  readOnly: boolean = false
) => type === 'radio' && readOnly;

export const Input: Component<InputTypeMap> = forwardRef(function Input(
  {
    type = 'text',
    className,
    startAdornment,
    endAdornment,
    label,
    disabled,
    checked,
    name,
    error,
    id,
    helpText,
    ...rest
  },
  ref
) {
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      togglePasswordVisibility();
    }
  };

  return (
    <Box className={className}>
      <Box className="flex">
        <Box
          className={cx(
            styles.group({
              row: isRadioOrCheck(type),
              ring: isRadioOrCheck(type),
              intent: getStyleType(type),
              error: hasError(error),
              disabled: disabled || isRadioReadOnly(type, !!rest?.readOnly),
            }),
            'flex-1'
          )}
        >
          {startAdornment ? (
            <Box
              className={styles.adornment({
                position: 'left',
                pb: getStyleType(type) === 'text',
              })}
            >
              {cloneElement(startAdornment, { disabled: !!disabled })}
            </Box>
          ) : null}

          <Box
            className={styles.inputWrapper({
              intent: getStyleType(type),
            })}
          >
            <Box
              as="input"
              {...rest}
              ref={ref}
              type={showPassword ? 'text' : type}
              className={styles.input({
                intent: getStyleType(type),
                disabled: !!disabled,
              })}
              disabled={disabled || isRadioReadOnly(type, !!rest?.readOnly)}
              checked={checked}
              placeholder=" "
              aria-describedby={
                helpText || error ? `${id || name}-desc` : undefined
              }
              name={name}
              id={id || name}
              aria-invalid={!!error ? 'true' : undefined}
            />
            <Label
              as="label"
              htmlFor={id || name}
              floating={getStyleType(type) === 'text'}
              rise={!!rest.value || type === 'date'}
              disabled={!!disabled}
              weight={getStyleType(type) !== 'text' ? 'medium' : undefined}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {React.isValidElement(label) && typeof label === 'object'
                ? { ...label }
                : !rest.required
                ? `${label}`
                : `${label} *`}
            </Label>
          </Box>
          {endAdornment ? (
            <Box
              className={styles.adornment({
                position: 'right',
                pb: getStyleType(type) === 'text',
              })}
            >
              {cloneElement(endAdornment, { disabled: !!disabled })}
            </Box>
          ) : null}
        </Box>
        {type === 'password' && (
          <Box
            as="span"
            className="ml-2 flex cursor-pointer items-end px-1"
            onClick={togglePasswordVisibility}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
          >
            {showPassword ? (
              <Eye aria-label="Hide password" width={23} height={23} />
            ) : (
              <EyeSlash aria-label="Show password" width={23} height={23} />
            )}
          </Box>
        )}
      </Box>
      {error ? <InputError error={error} id={`${name}-desc`} /> : null}
      {helpText ? (
        <Typography intent="label" className="mt-2" id={`${name}-desc`}>
          {helpText}
        </Typography>
      ) : null}
    </Box>
  );
});
